/* LoginSectionComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./LoginSectionRenderer");
const _ = require("lodash");
const GSO_ID = "565a65db-54e7-4461-a954-f0f38f310e19";

class LoginSectionComponent extends EventEmitter {
    constructor(logger, userService, docsService, userSession, router, SystemConfig, ControlUser) {
        super();
        this._logger = logger;
        this._userService = userService;
        this._docsService = docsService;
        this._userSession = userSession;
        this._router = router;
        this._challengeState = undefined;
        this._SystemConfig = SystemConfig;
        this._ControlUser = ControlUser;
        this._redirect = _.get(this._router.getRouteDetails(), "rd.redirect", false);
        if (!this._redirect && _.get(this._router.getRouteDetails(), "sln", false) === "classic") {
            this._redirect = {sln: "classic"};
        }
        this._ltk = _.get(this._router.getRouteDetails(), "rd.ltk", undefined);
        this.isLoggingIn = false; // Prevent multiple concurrent logins
        //Todo - this will be replaced with Andys FSM (Flying Spaghetti Monster).
        //controlAppNav.hideHomebar();

        this._renderer = new Renderer(this);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    _getDetails(type) {
        let meth = "get" + type;
        // console.log(this._docsService[meth]({}));
        return this._docsService[meth]({})
            .then((data) => data.content)
            .catch((err) => {
                this._logger.error(err);
                return "<div class='error-msg'>Unable to load. Please try again later.</div>";
            });
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        return this._renderer
            ._shutdown()
            .then(() => {
                this._renderer = undefined;
                this._logger = undefined;
                this._userSession = undefined;
                this._userService = undefined;
                this._docsService = undefined;
                this._challengeState = undefined;
                this._redirect = undefined;
                this._ltk = undefined;
                this.isLoggingIn = undefined;
            })
            .catch((err) =>
                this._logger.warn({error: err}, "Error shutting down LoginSectionComponent:")
            );
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    userRequest(email) {
        if (!_.isEmpty(email)) {
            this._userService.retrieveUserNameByEmail(
                {userEmail: email},
                {
                    Success: (this.onUserRequestSuccess = this.onUserRequestSuccess.bind(this)),
                    ValidationError: (this.onUserRequestError = this.onUserRequestError.bind(this)),
                    Failure: (this.onUserRequestFailure = this.onUserRequestFailure.bind(this)),
                }
            );
        }
    }

    onUserRequestSuccess(resp) {
        this.emit("user.request.success", resp);
        this.emit("info", "Your username has been sent to your email address.");
    }

    onUserRequestError(appErr) {
        if (appErr.error.errorCode === "ValidationError") {
            this.emit("error", "Please provide a valid email address. (GEC: InvalidEmailAddress)");
        } else {
            this.emit("info", "Your username has been sent to your email address.");
        }
    }

    onUserRequestFailure() {
        this.emit("info", "Your username has been sent to your email address.");
    }

    passwordRequest(email) {
        this._userService.sendResetPasswordByEmail(
            {userEmail: email},
            {
                Success: (this.onPasswordRequestSuccess = this.onPasswordRequestSuccess.bind(this)),
                ValidationError: (this.onPasswordRequestError =
                    this.onPasswordRequestError.bind(this)),
                Failure: (this.onPasswordRequestFailure = this.onPasswordRequestFailure.bind(this)),
            }
        );
    }

    onPasswordRequestSuccess(resp) {
        this.emit("password.request.success", resp);
        this.emit(
            "info",
            "Password reset instructions have been sent to the entered email address."
        );
    }

    onPasswordRequestError(appErr) {
        if (appErr.error.errorCode === "ValidationError") {
            this.emit("error", "Please provide a valid email address. (GEC: InvalidEmailAddress)");
        } else {
            this.emit(
                "info",
                "Password reset instructions have been sent to the entered email address."
            );
        }
    }

    onPasswordRequestFailure(reqErr) {
        let errCode = _.get(reqErr, "error.errorCode");

        if (errCode === "LdapUserResetError" || errCode === "RadiusUserResetError") {
            this.emit("error", "Password reset for this user is not managed by Gluware");
        } else {
            this.emit(
                "info",
                "Password reset instructions have been sent to the entered email address."
            );
        }
    }

    async logoutPreviousSession() {
        const token = localStorage.getItem("Steve.session.token");
        if (token) {
            localStorage.clear();
            await fetch(`${window.origin}/session/logout/${token}%2Ftimeout`, {
                method: "GET",
            });
        } else {
            return Promise.resolve();
        }
    }

    loginRequest(data) {
        // If a login request is currently in progress, cancel the new request
        if (this.isLoggingIn) {
            return;
        }
        this.isLoggingIn = true;

        // If a previous session is still active, close it before logging in with new credentials
        this.logoutPreviousSession().then(() => {
            this._userSession
                .login({
                    username: data.username,
                    password: data.password,
                    challengeState: this._challengeState,
                })
                .then((loginResponse) => {
                    if (loginResponse && loginResponse.challenge) {
                        this._challengeState = loginResponse.challengeState;
                        this.emit(
                            "info",
                            loginResponse.challengePrompt || "Please enter your challenge password",
                            true
                        );
                        this.emit("challenge");
                    } else {
                        this._challengeState = undefined;
                        const user = _.get(this._userSession, "user");
                        if (user) {
                            if (!user.eulaAcceptTime) {
                                this._router.goTo({
                                    app: "GameConsole",
                                    s: "Eula",
                                    rd: {redirect: this._redirect},
                                });
                                return false;
                            } else {
                                return this._SystemConfig.loadSystemConfig(GSO_ID);
                            }
                        }
                    }
                })
                .then((val) => {
                    if (!val) {
                        return val;
                    }
                    const banner = _.get(val, "banner");
                    if (banner && banner.enable) {
                        let bannerFromStorage = window.sessionStorage.getItem(
                            this._userSession.token + "isBannerAccepted"
                        );
                        if (
                            !this._userSession.user.bannerAcceptTime ||
                            (banner.displayMode === "ALWAYS" && !bannerFromStorage)
                        ) {
                            this._router.goTo({
                                app: "GameConsole",
                                s: "Banner",
                                rd: {triggeredBy: "Eula", redirect: this._redirect},
                            });
                        } else {
                            return true;
                        }
                    } else {
                        return true;
                    }
                })
                .then((val) => {
                    if (!val) {
                        return;
                    }
                    return this._ControlUser.loadControlUser(
                        this._userSession.user.tenantId,
                        this._userSession.user.id
                    );
                })
                .then((controluser) => {
                    if (!controluser) {
                        return;
                    }
                    const loadPath = window.localStorage.getItem("loadpath");
                    if (loadPath) {
                        window.localStorage.removeItem("loadpath");
                        window.location = loadPath;
                        return;
                    }
                    let redirect = _.get(this._router.routeDetails, "rd.redirect", false);
                    if (
                        redirect &&
                        redirect.s &&
                        redirect.s !== "LoginCenter" &&
                        redirect.sln !== "classic"
                    ) {
                        redirect.redirect = null;
                        this._router.goTo(redirect);
                    } else if (
                        (!_.isUndefined(this._router.routeDetails.sln) &&
                            this._router.routeDetails.sln === "classic") ||
                        redirect.sln === "classic"
                    ) {
                        window.location =
                            window.origin + "/_app_/gluware_control/#!/s/Perspective/rd/";
                    } else {
                        const lastSoln = controluser.lastRoute;
                        if (lastSoln) {
                            window.sessionStorage.setItem(
                                this._userSession.user.id + "lastPathInfo",
                                lastSoln
                            );
                        }
                        let lastPathInfo = window.sessionStorage.getItem(
                            this._userSession.user.id + "lastPathInfo"
                        );
                        if (lastPathInfo) {
                            if (lastPathInfo.search("/s/Perspective/") >= 0) {
                                window.location.href =
                                    window.origin + "/_app_/gluware_control/#!" + lastPathInfo;
                                return;
                            } else if (lastPathInfo.search("/frontend/") >= 0) {
                                window.location = window.origin + lastPathInfo;
                            } else {
                                window.location.hash = "#!" + lastPathInfo;
                                return;
                            }
                        }

                        this._router.goTo({app: "GameConsole", s: "Dashboard", sln: null});
                    }
                })
                .catch((err) => {
                    this.isLoggingIn = false;
                    this._logger.error(err);
                    if (_.has(err, "error.errorCode") && _.isString(err.error.errorMsg)) {
                        let message = "";
                        switch (err.error.errorCode) {
                            case "InvalidUserPassword":
                                message = "Invalid username or password.";
                                break;
                            case "PasswordResetRequired":
                                message =
                                    "Your password has been reset. Please follow the instructions found in your email.";
                                break;
                            default:
                                message = "Unable to authenticate. ";
                                if (_.isString(err.error.errorMsg)) {
                                    message += err.error.errorMsg;
                                } else if (_.isString(err.error.message)) {
                                    message += err.error.message;
                                }
                                break;
                        }
                        this.emit("error", message);
                    } else {
                        this.emit("error", "Unable to authenticate user.");
                    }
                })
                .finally(() => {
                    this.isLoggingIn = false;
                });
        });
    }

    // rd.token is reserved by StartupAppendage/UserSession as a one-time OTK credential; ltk must stay distinct from it.
    async checkLoginOptions() {
        try {
            const response = await fetch(`${window.origin}/session/loginOptions`, {
                method: "POST",
                redirect: "error",
                headers: {"Content-Type": "application/json", Accept: "application/json"},
                body: JSON.stringify(this._ltk ? {ltk: this._ltk} : {}),
            });
            if (!response.ok) {
                throw new Error(`loginOptions failed with status ${response.status}`);
            }
            const {allowLocalLogin} = await response.json();
            return allowLocalLogin !== false;
        } catch (err) {
            _.invoke(
                this._logger,
                "warn",
                {error: err},
                "Unable to retrieve login options, allowing form."
            );
            return true;
        }
    }

    getContent() {
        return Promise.resolve()
            .then(() => {
                let version = this.getVersion();
                let eula = this.getEula();
                let terms = this.getTermsOfService();
                let privacy = this.getPrivacyPolicy();
                return Promise.all([version, eula, terms, privacy]);
            })
            .then((results) => {
                return [
                    {title: `&copy; ${new Date().getFullYear()} Gluware, Inc.`},
                    {title: results[0]}, // version
                    {title: "Privacy", details: results[3]},
                    {title: "Terms", details: results[2]},
                    {title: "EULA", details: results[1]},
                ];
            })
            .catch((err) => {
                console.log("Rejecting getContent promise", err);
                throw err;
            });
    }

    get privacyPolicy() {
        return this.getPrivacyPolicy();
    }

    getPrivacyPolicy() {
        return this._getDetails("PrivacyPolicy");
    }

    get termsOfService() {
        return this.getTermsOfService();
    }

    getTermsOfService() {
        return this._getDetails("TermsOfService");
    }

    get eula() {
        return this.getEula();
    }

    getEula() {
        return this._getDetails("Eula");
    }

    get version() {
        return this.getVersion();
    }

    getVersion() {
        return this._docsService
            .getVersion({})
            .then((VersionObj) => {
                let versionString = "v2.X.X";
                let parsedVersionObj = JSON.parse(VersionObj);
                if (!_.isEmpty(VersionObj)) {
                    let dateString = Date.create(
                        parsedVersionObj.gluware_version.lastUpdated
                    ).format();
                    versionString =
                        "v" + parsedVersionObj.gluware_version.semver + " (" + dateString + ")";
                }
                return versionString;
            })
            .catch((err) => {
                this._logger.error(err, "Error getting system info version.");
                return "v2.X.X";
            });
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

LoginSectionComponent.$inject = [
    "$teve.logger",
    "$teve.services.User",
    "$teve.services.Docs",
    "$teve.userSession",
    "$teve.router",
    "$teve.entities.SystemConfig",
    "$teve.entities.ControlUser",
];
module.exports = LoginSectionComponent;
